import { createContext, useContext, useEffect, useState } from "react";
import { THEME } from "../../common/styles/theme";

const ThemeContext = createContext(undefined);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState("light");

  const changeTheme = (newTheme) => {
    if (newTheme === "light" || newTheme === "dark") {
      setTheme(newTheme);
      localStorage.setItem("theme", newTheme);
    } else {
      console.error("Invalid theme value:", newTheme);
    }
  };

  useEffect(() => {
    try {
      const storedTheme = localStorage.getItem("theme");
      if (storedTheme === "light" || storedTheme === "dark") {
        setTheme(storedTheme);
      }
    } catch (error) {
      console.error("Failed to load theme from localStorage", error);
    }
  }, []);

  useEffect(() => {
    // Update CSS custom properties when theme changes
    const root = document.documentElement;
    if (theme === "dark") {
      root.setAttribute("data-theme", "dark");
    } else {
      root.removeAttribute("data-theme");
    }

    // Apply custom primary color if it exists
    const customPrimaryColor = localStorage.getItem("customPrimaryColor");
    if (customPrimaryColor) {
      root.style.setProperty("--primary-color", customPrimaryColor);
    } else {
      root.style.removeProperty("--primary-color");
    }
  }, [theme]);

  // Listen for storage changes to update primary color in real-time
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "customPrimaryColor") {
        const root = document.documentElement;
        if (e.newValue) {
          root.style.setProperty("--primary-color", e.newValue);
        } else {
          root.style.removeProperty("--primary-color");
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, changeTheme, colors: THEME[theme] }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
